import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'src/utils/Hooks';
import { trackExport } from 'src/utils/mixpanel';
import {
  useBaseMetricRequestBuilder,
  UseBaseMetricRequestBuilderProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { UseGenericAdvancedSearchParameters } from 'src/utils/Hooks/useGenericAdvancedSearch';
import axios from 'axios';

import { AggregationField } from 'src/types/application/types';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { getAppName } from 'src/utils/app';

export interface AdvancedSearchResponse<TMetadata> {
  aggregations: {
    [byGroupByFieldName: string]: {
      additionalValues: {
        [key: string]: number;
      };
      additionalMetaData: TMetadata;
    }[];
  };
}

export interface ExportProps
  extends Omit<
  Required<UseBaseMetricRequestBuilderProps>,
  | 'startWeekId'
  | 'endWeekId'
  | 'buildAggregationBuilder'
  | 'sortFieldName'
  | 'groupByFieldName'
  | 'indexName'
  | 'overrideDependentFields'
  > {
  sortFieldName?: string;
  groupBy: string;
  aggregationKey?: string;
  includeCompTimePeriod?: boolean;
  isSeparateWeek?: boolean;
  isSeparateDay?: boolean;
  indexName?: string;
  secondaryGroupBy?: string;
  overrideDependentFields?: boolean;
  /**
   * Optionally modify the request builder before sending the request
   */
  buildRequest?: (requestBuilder: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder;

  /**
   * Optionally modify the aggregation in the builder request.
   */
  buildAggregation?: (aggregation: AggregationBuilder) => AggregationBuilder;

  /**
   * Optionally filter the response data
   */
  startWeekId?: number;
  endWeekId?: number;
  useGenericAdvancedSearchArgs?: Partial<UseGenericAdvancedSearchParameters>;
  customComparisonStartWeekId?: number;
  customComparisonEndWeekId?: number;
}

export const useBuildExportRequest = ({
  fields,
  groupBy,
  indexName,
  requestId,
  sortFieldName,
  startWeekId,
  endWeekId,
  buildAggregation,
  customComparisonStartWeekId,
  customComparisonEndWeekId,
  includeCompTimePeriod,
  isSeparateWeek,
  isSeparateDay,
  secondaryGroupBy,
  buildRequest,
  overrideDependentFields
}: ExportProps) => {
  const { startWeek: mainStartWeek, endWeek: mainEndWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: compareStartWeek, endWeek: compareEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  let groupByFieldName = secondaryGroupBy ? `${groupBy},${secondaryGroupBy}` : groupBy;
  groupByFieldName = isSeparateWeek ? `${groupByFieldName},weekId` : groupByFieldName;
  groupByFieldName = isSeparateDay ? `${groupByFieldName},dayId` : groupByFieldName;

  const user = useAppSelector((state) => state.user);

  const sortField: AggregationField | null = useMemo(() => {
    return sortFieldName ? INDEX_FIELDS.getField(getAppName(), indexName, sortFieldName) : null;
  }, [indexName, sortFieldName]);

  const buildAggregationBuilder = useCallback(
    (aggBuilder: AggregationBuilder) => {
      if (includeCompTimePeriod) {
        aggBuilder.addComparisonRangeFilter(
          'weekId',
          customComparisonStartWeekId || compareStartWeek,
          customComparisonEndWeekId || compareEndWeek
        );
      }

      if (sortField) {
        aggBuilder.setSortByAggregationField(
          sortField.displayName,
          sortField.name,
          sortField.aggregationFunction,
          true
        );
        aggBuilder.setSortDirection('desc');
      }
      if (buildAggregation) {
        return buildAggregation(aggBuilder);
      }
      return aggBuilder;
    },
    [
      buildAggregation,
      compareEndWeek,
      compareStartWeek,
      customComparisonEndWeekId,
      customComparisonStartWeekId,
      includeCompTimePeriod,
      sortField
    ]
  );
  const requestBuilder = useBaseMetricRequestBuilder({
    requestId,
    fields,
    indexName,
    groupByFieldName,
    endWeekId: endWeekId || mainEndWeek,
    startWeekId: startWeekId || mainStartWeek,
    buildAggregationBuilder,
    overrideDependentFields
  });

  const requestBody = useMemo(() => {
    requestBuilder.setProcessDocuments(true);
    if (sortField) {
      requestBuilder.addSortField(sortField.displayName, sortField.name, sortField.aggregationFunction as string, true);
    }
    if (buildRequest) {
      return buildRequest(requestBuilder).build();
    }
    return requestBuilder.build();
  }, [buildRequest, requestBuilder, sortField]);

  const exportCallback = async () => {
    return axios.post(`api/${getAppName()}/AdvancedSearchExport`, [requestBody]).then(() => {
      trackExport(window.location, {}, user);
    });
  };
  return { exportCallback };
};
